import { UNICODE } from '@shared/models/unicode.model';
import { getUserLocale, isLocaleSupported } from '../locale.helpers';
import { guards } from '../typeguards.helpers';
import { getScientificDisplayNumber } from './get-scientific-display-number.helper';
import { getCompactDisplayNumber } from './get-compact-display-number.helper';

type Notation = Extract<
  Intl.NumberFormatOptions['notation'],
  'standard' | 'compact'
>;
type FormatNumberOptions = {
  decimals?: number;
  locale?: `${string}-${string}` | 'NodonFormat';
  notation?: Notation;
};

const getDisplayNumberBase = (
  /* Number to format */
  value: number,
  /* Options for the formatting. Locale is default "NodonFormat" */
  options: FormatNumberOptions = {},
): string => {
  const { decimals, locale = 'NodonFormat', notation = 'standard' } = options;

  if (locale === 'NodonFormat')
    return _getNodonDisplayNumber(value, { decimals, notation });

  try {
    return Intl.NumberFormat(
      isLocaleSupported(locale) ? locale : getUserLocale(),
      { ...getNodonFormatFractionDigitsConfig(value, decimals), notation },
    ).format(value);
  } catch (error) {
    console.error('Error formatting number', value, decimals, locale, error);
    return String(value);
  }
};

export const getDisplayNumber = Object.assign(getDisplayNumberBase, {
  /**
   * Binds formatting {@link FormatNumberOptions} into a unary formatter, handy
   * for `format`-style props (e.g. {@link getDisplayNumber}.withOptions({ notation: 'compact' })).
   */
  withOptions:
    (options: FormatNumberOptions) =>
    (value: number): string =>
      getDisplayNumberBase(value, options),
});

const SCIENTIFIC_FORMAT_OVER_THRESHOLD = 1e6;
const SCIENTIFIC_FORMAT_UNDER_THRESHOLD = 1e-6;

export type FormatDisplayNumberOptions = {
  /**
   * Number of decimals to display. If not provided, it will be determined based on the value.
   * @default undefined
   */
  decimals?: number;
};

type _GetNodonDisplayNumberOptions = {
  decimals?: number;
  notation: Notation;
};
/**
 * internal function for formatting numbers according to Nodon number format rules
 * do not use this function directly, use {@link getDisplayNumber} instead
 */
const _getNodonDisplayNumber = (
  value: number,
  { decimals, notation }: _GetNodonDisplayNumberOptions = {
    notation: 'standard',
  },
): string => {
  try {
    // Catch infinities and NaN
    if (!guards.is.finiteNumber(value)) return String(value);

    const absValue = Math.abs(value);

    // use nodon compact format
    if (notation === 'compact') return getCompactDisplayNumber(value, decimals);

    // extremes: scientific notation for abs ≥ 1e6 or abs < 1e-6
    if (notation === 'standard' && absValue >= SCIENTIFIC_FORMAT_OVER_THRESHOLD)
      return getScientificDisplayNumber(value);
    if (notation === 'standard' && absValue < SCIENTIFIC_FORMAT_UNDER_THRESHOLD)
      return getScientificDisplayNumber(value);

    // default fractionDigits (will only be used for small numbers)
    const decimalConf = getNodonFormatFractionDigitsConfig(value, decimals);

    // We want to use baseLocale, but if not supported, we use the user locale
    const baseLocale = 'sv-SE';
    const selectedLocale = isLocaleSupported(baseLocale)
      ? baseLocale
      : getUserLocale();

    const rawFormatted = Intl.NumberFormat(selectedLocale, {
      notation,
      ...decimalConf,
    }).format(value);

    if (baseLocale !== selectedLocale) return rawFormatted;
    // if selectedLocale is sv-SE, we want to replace the comma with a dot
    return rawFormatted
      .replace(',', '.')
      .replaceAll(/\s/g, UNICODE.SPACES.NARROW_NON_BREAKING);
  } catch {
    console.error('Error formatting number', value, decimals);
    return String(value);
  }
};

type DecimalConfig = Pick<
  Intl.NumberFormatOptions,
  'minimumFractionDigits' | 'maximumFractionDigits' | 'maximumSignificantDigits'
>;

/**
 * Returns the fraction digits config for a number in nodon format
 * Also sets the maximum significant digits to 2 for small numbers
 * @param value
 * @param decimals
 * @returns DecimalConfig - intended to be passed to Intl.NumberFormat
 */
const getNodonFormatFractionDigitsConfig = (
  value: number,
  decimals?: number,
): DecimalConfig => {
  const absValue = Math.abs(value);
  if (guards.is.number(decimals)) {
    // consumer dictated strict amount of decimals
    return {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
  }
  if (absValue >= 10) return { maximumFractionDigits: 0 };
  if (absValue >= 1) return { maximumFractionDigits: 1 };
  return { maximumSignificantDigits: 2 }; // for small numbers, set significant digits to 2
};
